import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { refFetch } from '@/lib/refCache';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  CalendarClock, AlertTriangle, CalendarDays, Wallet,
  FileSpreadsheet, FileDown, Loader2, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const STATUS_META = {
  upcoming:  { label: 'Upcoming',  color: 'bg-sky-500/15 text-sky-300 border-sky-500/40',       icon: CalendarClock },
  due_today: { label: 'Due Today', color: 'bg-amber-500/15 text-amber-300 border-amber-500/40', icon: CalendarDays  },
  overdue:   { label: 'Overdue',   color: 'bg-rose-500/15 text-rose-300 border-rose-500/40',    icon: AlertTriangle },
};

function fmt(n) { return `PKR ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`; }
function d2(s) { if (!s) return '—'; const d = new Date(s); return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }

export default function InstallmentReports({ initialStatus = 'overdue' }) {
  const { user } = useAuth();
  const isHQ = user?.role === 'Super Admin' || user?.role === 'Admin';
  // Iter33 — Outstanding money numbers are for Admin / Managers only.
  const canSeeAmounts = ['Super Admin', 'Admin', 'Branch Manager', 'Accountant'].includes(user?.role);

  const [summary, setSummary]   = useState(null);
  const [status, setStatus]     = useState(initialStatus);
  const [branches, setBranches] = useState([]);
  const [users, setUsers]       = useState([]);
  const [branchId, setBranchId] = useState(isHQ ? 'all' : (user?.branch_id || ''));
  const [seId, setSeId]         = useState('all');
  const [roId, setRoId]         = useState('all');
  const [search, setSearch]     = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [rows, setRows]         = useState([]);
  const [totals, setTotals]     = useState(null);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(null);

  // ------------------------------------------------------------- fetches
  const loadRefs = async () => {
    try {
      const [b, u] = await Promise.all([
        refFetch('/api/branches'),
        refFetch('/api/users/mini').catch(() => []),
      ]);
      setBranches(b || []);
      setUsers(u || []);
    } catch { /* ignore */ }
  };

  const loadSummary = async () => {
    try {
      const params = new URLSearchParams();
      const effBranch = isHQ ? branchId : (user?.branch_id || '');
      if (effBranch && effBranch !== 'all') params.set('branch_id', effBranch);
      const { data } = await axios.get(`${API_URL}/api/reports/installments/summary?${params}`,
        { withCredentials: true });
      setSummary(data);
    } catch { setSummary(null); }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, page: String(page), page_size: String(PAGE_SIZE) });
      const effBranch = isHQ ? branchId : (user?.branch_id || '');
      if (effBranch && effBranch !== 'all') params.set('branch_id', effBranch);
      if (seId && seId !== 'all') params.set('se_id', seId);
      if (roId && roId !== 'all') params.set('ro_id', roId);
      if (search.trim()) params.set('search', search.trim());
      if (fromDate) params.set('from_date', fromDate);
      if (toDate)   params.set('to_date', toDate);
      const { data } = await axios.get(`${API_URL}/api/reports/installments?${params}`,
        { withCredentials: true });
      setRows(data.rows || []);
      setTotals(data.totals || null);
      setPages(data.total_pages || 1);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadSummary(); }, [branchId]);
  useEffect(() => { setPage(1); }, [status, branchId, seId, roId, fromDate, toDate]);
  useEffect(() => { loadRows(); }, [status, branchId, seId, roId, fromDate, toDate, page]);

  const doExport = async (fmt) => {
    try {
      setExporting(fmt);
      const params = new URLSearchParams({ status, fmt });
      const effBranch = isHQ ? branchId : (user?.branch_id || '');
      if (effBranch && effBranch !== 'all') params.set('branch_id', effBranch);
      if (seId && seId !== 'all') params.set('se_id', seId);
      if (roId && roId !== 'all') params.set('ro_id', roId);
      if (search.trim()) params.set('search', search.trim());
      if (fromDate) params.set('from_date', fromDate);
      if (toDate)   params.set('to_date', toDate);
      const { data } = await axios.get(`${API_URL}/api/reports/installments/export?${params}`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `installments_${status}_${ts}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(null); }
  };

  const salesExecs      = useMemo(() => users.filter(u => u.role === 'Sales Executive'),   [users]);
  const recoveryOfficers = useMemo(() => users.filter(u => u.role === 'Recovery Officer'), [users]);

  const cards = [
    { key: 'upcoming',  title: 'Upcoming',  amount: summary?.upcoming?.amount,  count: summary?.upcoming?.count,  color: 'from-sky-600/25 to-sky-500/5 border-sky-500/40',    icon: CalendarClock, iconColor: 'text-sky-400' },
    { key: 'due_today', title: 'Due Today', amount: summary?.due_today?.amount, count: summary?.due_today?.count, color: 'from-amber-600/25 to-amber-500/5 border-amber-500/40', icon: CalendarDays,  iconColor: 'text-amber-400' },
    { key: 'overdue',   title: 'Overdue',   amount: summary?.overdue?.amount,   count: summary?.overdue?.count,   color: 'from-rose-600/25 to-rose-500/5 border-rose-500/40',   icon: AlertTriangle, iconColor: 'text-rose-400' },
  ];

  return (
    <div className="space-y-6" data-testid="installment-reports">
      {/* -------- Summary cards -------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const active = status === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setStatus(c.key)}
              data-testid={`inst-report-card-${c.key}`}
              className={`text-left rounded-sm border p-4 bg-gradient-to-br ${c.color} transition-all
                ${active ? 'ring-2 ring-white/60 -translate-y-0.5' : 'hover:-translate-y-0.5'}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm uppercase tracking-wider">{c.title}</span>
                <c.icon className={`h-5 w-5 ${c.iconColor}`} />
              </div>
              {canSeeAmounts ? (
                <>
                  <div className="mt-3 text-2xl font-bold text-white">{fmt(c.amount)}</div>
                  <div className="text-xs text-slate-400 mt-1">{(c.count ?? 0)} installments</div>
                </>
              ) : (
                <>
                  <div className="mt-3 text-3xl font-bold text-white">{(c.count ?? 0).toLocaleString()}</div>
                  <div className="text-xs text-slate-400 mt-1">installments</div>
                </>
              )}
            </button>
          );
        })}
        {canSeeAmounts && (
          <div className="rounded-sm border border-slate-700 bg-slate-900 p-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm uppercase tracking-wider">Total Outstanding</span>
              <Wallet className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="mt-3 text-2xl font-bold text-white" data-testid="inst-report-total-outstanding">
              {fmt(summary?.total_outstanding)}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Sum of every unpaid installment (fully paid excluded)
            </div>
          </div>
        )}
      </div>

      {/* -------- Filter bar -------- */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <h3 className="text-white font-semibold flex items-center">
            <Search className="h-4 w-4 mr-2 text-slate-400" />
            Filters — {STATUS_META[status]?.label || 'All'}
          </h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            {isHQ && (
              <div className="col-span-1">
                <Label className="text-xs text-slate-400">Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="inst-filter-branch">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white">
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-400">Sales Executive</Label>
              <Select value={seId} onValueChange={setSeId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="inst-filter-se">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  <SelectItem value="all">All SE</SelectItem>
                  {salesExecs.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Recovery Officer</Label>
              <Select value={roId} onValueChange={setRoId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="inst-filter-ro">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  <SelectItem value="all">All RO</SelectItem>
                  {recoveryOfficers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Due From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white" data-testid="inst-filter-from" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Due To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white" data-testid="inst-filter-to" />
            </div>
            <div className="lg:col-span-1">
              <Label className="text-xs text-slate-400">Search</Label>
              <Input placeholder="Name / mobile / CNIC / A/C #"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); loadRows(); } }}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="inst-filter-search" />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between mt-4 gap-3">
            <div className="text-sm text-slate-400">
              {totals && (<>
                Showing <span className="text-white">{rows.length}</span> of{' '}
                <span className="text-white">{totals.count}</span>
                {canSeeAmounts && <> · Outstanding <span className="text-white">{fmt(totals.outstanding_amount)}</span></>}
              </>)}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { setPage(1); loadRows(); }} variant="outline"
                className="border-slate-700 text-slate-200" data-testid="inst-apply-filters">
                <Search className="h-4 w-4 mr-2" /> Apply
              </Button>
              <Button onClick={() => doExport('xlsx')} disabled={exporting === 'xlsx'}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="inst-export-xlsx">
                {exporting === 'xlsx' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                Excel
              </Button>
              <Button onClick={() => doExport('csv')} disabled={exporting === 'csv'}
                className="bg-blue-600 hover:bg-blue-700" data-testid="inst-export-csv">
                {exporting === 'csv' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* -------- Data table -------- */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-800">
                  <TableHead className="text-slate-300">Customer</TableHead>
                  <TableHead className="text-slate-300">A/C #</TableHead>
                  <TableHead className="text-slate-300">Agreement</TableHead>
                  <TableHead className="text-slate-300">Branch</TableHead>
                  <TableHead className="text-slate-300">SE / RO</TableHead>
                  <TableHead className="text-slate-300 text-center">#</TableHead>
                  <TableHead className="text-slate-300">Due Date</TableHead>
                  {canSeeAmounts && <TableHead className="text-slate-300 text-right">Amount</TableHead>}
                  {canSeeAmounts && <TableHead className="text-slate-300 text-right">Paid</TableHead>}
                  {canSeeAmounts && <TableHead className="text-slate-300 text-right">Outstanding</TableHead>}
                  <TableHead className="text-slate-300 text-center">Days</TableHead>
                  <TableHead className="text-slate-300 text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-12">
                    <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading…
                  </TableCell></TableRow>
                )}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-12">
                    No installments match the selected filters.
                  </TableCell></TableRow>
                )}
                {!loading && rows.map((r, i) => {
                  const meta = STATUS_META[r.status] || STATUS_META.upcoming;
                  return (
                    <TableRow key={`${r.installment_id}-${r.installment_no}-${i}`}
                      className="border-slate-800 hover:bg-slate-800/40"
                      data-testid={`inst-row-${i}`}>
                      <TableCell className="text-white font-medium">
                        {r.customer_name}
                        <div className="text-xs text-slate-500">{r.customer_mobile || r.customer_cnic}</div>
                      </TableCell>
                      <TableCell className="text-slate-300">{r.account_number || '—'}</TableCell>
                      <TableCell className="text-slate-300">{r.agreement_number || '—'}</TableCell>
                      <TableCell className="text-slate-300">{r.branch_name || '—'}</TableCell>
                      <TableCell className="text-slate-300">
                        {r.sales_executive || '—'}
                        <div className="text-xs text-slate-500">{r.recovery_officer || ''}</div>
                      </TableCell>
                      <TableCell className="text-center text-slate-300">
                        {r.is_down_payment ? <Badge className="bg-slate-700 text-white">DP</Badge> : `#${r.installment_no}`}
                      </TableCell>
                      <TableCell className="text-slate-300">{d2(r.due_date)}</TableCell>
                      {canSeeAmounts && <TableCell className="text-right text-slate-300">{fmt(r.amount_due)}</TableCell>}
                      {canSeeAmounts && <TableCell className="text-right text-emerald-300">{fmt(r.paid_amount)}</TableCell>}
                      {canSeeAmounts && <TableCell className="text-right text-white font-semibold">{fmt(r.outstanding)}</TableCell>}
                      <TableCell className="text-center text-slate-300">{r.days}</TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center gap-1 border px-2 py-0.5 rounded-full text-xs ${meta.color}`}>
                          <meta.icon className="h-3 w-3" /> {meta.label}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* -------- Pagination -------- */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-400">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="border-slate-700 text-slate-200" data-testid="inst-page-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages}
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              className="border-slate-700 text-slate-200" data-testid="inst-page-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
